import { useEffect, useState } from 'react';
import { Button, Modal, Table, Tag } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';

import { reset } from 'slices/documentSlice';
import { closeModal } from 'slices/modalsSlice';
import { fetchDocumentVersions } from 'slices/documentsActions';
import { useAppDispatch, useAppSelector } from 'utilities/hooks';
import { useAxios } from 'utilities/axios';
import { Document } from 'interfaces/documents';
import TableColumns from './TableColumns';

export interface IDocumentVersionsModalProps {
    document: Document;
};

export interface IDocumentVersionsModalCallbacks {
    onCancel?: (value: Boolean) => void;
    onClose?: () => void;
};

const DocumentVersionsModal = ({ id, zIndex, props, callbacks }: { id: string, zIndex: number, props: IDocumentVersionsModalProps, callbacks: IDocumentVersionsModalCallbacks; }) => {
    const dispatch = useAppDispatch();
    const { document } = props;
    const { onCancel, onClose } = callbacks;

    const accessToken = useAppSelector((state) => state.auth.accessToken);
    const currentTenant = useAppSelector((state) => state.tenants.currentTenant);
    const axios = useAxios(accessToken, currentTenant);

    const [isOpen, setIsOpen] = useState(false);
    const [documentWithAccess, setDocumentWithAccess] = useState<Document>(document);
    const { loading, versions } = useAppSelector((state) => state.document);

    useEffect(() => {
        if (!id || !document) return;

        setIsOpen(true);
        dispatch(fetchDocumentVersions(document.id));

        (async () => {
            try {
                const response = await axios?.get(`api/v1/list/documentAccess?documentId=${document.id}`, {});

                setDocumentWithAccess({
                    ...document,
                    access: response?.data?.data?.documentAccess,
                });
            } catch (error) {
                setDocumentWithAccess({
                    ...document,
                    access: 'viewer',
                });
            }
        })();
    }, [id, document]);

    const close = () => {
        dispatch(closeModal({ modal: 'documentVersionsModal', id }));
    };

    const handleClose = () => {
        dispatch(reset());
        onClose?.();
        close();
    };

    const handleCancel = () => {
        onCancel?.(false);
        close();
    };

    return <Modal
        zIndex={zIndex}
        title={
            <>
                Versions of{" "}
                <Tag color="blue" style={{ marginLeft: 8 }}>
                    <FileTextOutlined style={{ marginRight: 4 }} />
                    {document?.name}
                </Tag>
            </>
        }
        open={isOpen}
        onCancel={handleClose}
        footer={
            <>
                <Button
                    color="primary"
                    onClick={() => handleCancel()}
                >
                    Close
                </Button>
            </>
        }
        width={1000}
    >
        <Table
            columns={TableColumns({ document: documentWithAccess, closeModal: close })}
            dataSource={versions}
            loading={loading}
            pagination={false}
        />
    </Modal>;
};

export default DocumentVersionsModal;
